import React, { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import {
	Stepper,
	Step,
	StepLabel,
	Typography,
	CircularProgress,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialog from "../ChatiaDialog";

const useStyles = makeStyles((theme) => ({
	loadingWrap: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: "32px 0",
	},
	emptyState: {
		...theme.chatiaDS.empty,
	},
	stepper: {
		background: "transparent",
		padding: 0,
		"& .MuiStepConnector-vertical": {
			marginLeft: 11,
			padding: "4px 0",
		},
		"& .MuiStepIcon-root": {
			color: theme.mode === "light" ? "#cbd5e1" : "#3a4f5c",
		},
		"& .MuiStepIcon-active, & .MuiStepIcon-completed": {
			color: theme.palette.primary.main,
		},
		"& .MuiStepLabel-iconContainer": {
			paddingRight: 12,
		},
	},
	stepLabel: {
		"& .MuiStepLabel-label": {
			fontSize: 13.5,
			color: theme.chatiaDS.colors.textPrimary,
		},
	},
	logTime: {
		fontSize: 11,
		color: theme.chatiaDS.colors.textSecondary,
		marginTop: 2,
	},
}));

const ShowTicketLogModal = ({ isOpen, handleClose, ticketId }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [logs, setLogs] = useState([]);

	const typeDescriptions = {
		create: i18n.t("showTicketLogModal.options.create"),
		chatBot: i18n.t("showTicketLogModal.options.chatBot"),
		queue: i18n.t("showTicketLogModal.options.queue"),
		open: i18n.t("showTicketLogModal.options.open"),
		access: i18n.t("showTicketLogModal.options.access"),
		transfered: i18n.t("showTicketLogModal.options.transfered"),
		receivedTransfer: i18n.t("showTicketLogModal.options.receivedTransfer"),
		pending: i18n.t("showTicketLogModal.options.pending"),
		closed: i18n.t("showTicketLogModal.options.closed"),
		reopen: i18n.t("showTicketLogModal.options.reopen"),
		redirect: i18n.t("showTicketLogModal.options.redirect"),
	};

	useEffect(() => {
		if (!isOpen) return;
		setLoading(true);
		const delay = setTimeout(async () => {
			try {
				const { data } = await api.get(`/tickets-log/${ticketId}`);
				setLogs(data);
			} catch (err) {
				toastError(err);
			}
			setLoading(false);
		}, 300);
		return () => clearTimeout(delay);
	}, [ticketId, isOpen]);

	const renderLogLine = (log) => {
		const subject =
			log.type === "access" ||
			log.type === "transfered" ||
			log.type === "open" ||
			log.type === "pending" ||
			log.type === "closed" ||
			log.type === "reopen"
				? log?.user?.name
				: log?.type === "queue" || log?.type === "redirect"
					? log?.queue?.name
					: log.type === "receivedTransfer"
						? `${log?.queue?.name} - ${log?.user?.name}`
						: "";
		return `${subject ? subject + " " : ""}${typeDescriptions[log.type] || log.type}`;
	};

	return (
		<ChatiaDialog
			open={isOpen}
			onClose={handleClose}
			title={i18n.t("showTicketLogModal.title.header")}
			maxWidth="sm"
			hideFooter
		>
			{loading ? (
				<div className={classes.loadingWrap}>
					<CircularProgress size={28} />
				</div>
			) : logs.length === 0 ? (
				<div className={classes.emptyState}>
					{i18n.t("showTicketLogModal.empty")}
				</div>
			) : (
				<Stepper
					activeStep={logs.length - 1}
					orientation="vertical"
					className={classes.stepper}
				>
					{logs.map((log, index) => (
						<Step key={index} active completed>
							<StepLabel className={classes.stepLabel}>
								{renderLogLine(log)}
								<Typography component="div" className={classes.logTime}>
									{format(parseISO(log?.createdAt), "dd/MM/yyyy HH:mm")}
								</Typography>
							</StepLabel>
						</Step>
					))}
				</Stepper>
			)}
		</ChatiaDialog>
	);
};

export default ShowTicketLogModal;
